import { Injectable } from '@angular/core';
import { Action, Store, select } from '@ngrx/store';
import { Effect, Actions, ofType } from '@ngrx/effects';

import { Observable } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { authSelectors } from '@content-platform/web-angular-shared-util-auth';

import { Application } from '../models';
import { applicationActions } from '../actions';
import { ApplicationResource } from '../services';
import { ApplicationsState } from '../reducers';

/**
 * The application effects imported in {@link ApplicationApiModule }
 *
 */
@Injectable()
export class ApplicationEffects {
  constructor(
    private applicationActions$: Actions,
    private api: ApplicationResource,
    private store: Store<ApplicationsState>
  ) {}

  /**
   * Loads all the availabe applications only when a user is logged in and triggers a LoadSuccess action.
   *
   * @returns { Observable<Action> } Observable with LoadSuccess action when all applications are retrieved
   */
  @Effect()
  load$: Observable<Action> = this.applicationActions$.pipe(
    ofType(applicationActions.LOAD),
    switchMap(() =>
      this.api.query().pipe(
        map((apps: Application[]) => new applicationActions.LoadSuccess(apps)),
        catchError((error) => [
          new applicationActions.LoadFailed({
            error: error,
            message: 'Unable to load applications',
          }),
        ])
      )
    )
  );

  /**
   * Loads specific application only when a user is logged in and triggers a LoadByIdSuccess action.
   *
   * @returns { Observable<Action> } Observable with LoadByIdSuccess action when all applications are retrieved
   */
  @Effect()
  loadById$: Observable<Action> = this.applicationActions$.pipe(
    ofType(applicationActions.LOAD_BY_ID),
    switchMap((action: applicationActions.LoadById) =>
      this.api.queryById(action.payload).pipe(
        map((apps: Application) => new applicationActions.LoadByIdSuccess(apps)),
        catchError((error) => [
          new applicationActions.LoadByIdFailed({
            error: error,
            message: 'Unable to load application',
          }),
        ])
      )
    )
  );

  /**
   * Updates the application and triggers a UpdateSuccess action.
   *
   * @returns { Observable<Action> } Observable with UpdateSuccess action with the updated application
   */
  @Effect()
  update$: Observable<Action> = this.applicationActions$.pipe(
    ofType(applicationActions.UPDATE),
    map((action: applicationActions.Update) => action.payload),
    withLatestFrom(this.store.pipe(select(authSelectors.getUserId))),
    switchMap(([application, userId]) => {
      application.lastModifiedBy = userId;
      return this.api.update(application).pipe(
        map((app: Application) => new applicationActions.UpdateSuccess(app)),
        catchError((error) => [
          new applicationActions.UpdateFailed({
            error: error,
            message: 'Unable to update application',
          }),
        ])
      );
    })
  );

  /**
   * Create new application and CreateSuccess action.
   *
   * @returns { Observable<Action> } Observable with CreateSuccess action with the updated application
   */
  @Effect()
  create$: Observable<Action> = this.applicationActions$.pipe(
    ofType(applicationActions.CREATE),
    map((action: applicationActions.Update) => action.payload),
    withLatestFrom(this.store.pipe(select(authSelectors.getUserId))),
    switchMap(([app, userId]) => {
      app.lastModifiedBy = userId;
      app.createdBy = userId;
      return this.api.create(app).pipe(
        map((newApp: Application) => new applicationActions.CreateSuccess(newApp)),
        catchError((error) => [
          new applicationActions.CreateFailed({
            error: error,
            message: 'Unable to create application',
          }),
        ])
      );
    })
  );

  /**
   * Delete application and triggers DeleteSuccess action.
   *
   * @returns { Observable<Action> } Observable with DeleteSuccess action with the deleted app id
   */
  @Effect()
  delete$: Observable<Action> = this.applicationActions$.pipe(
    ofType(applicationActions.DELETE),
    map((action: applicationActions.Delete) => action.payload),
    switchMap((appId: number) =>
      this.api.delete(appId).pipe(
        map(() => new applicationActions.DeleteSuccess(appId)),
        catchError((error) => [
          new applicationActions.DeleteFailed({
            error: error,
            message: 'Unable to delete application',
          }),
        ])
      )
    )
  );
}
